import {Fragment, useEffect, useLayoutEffect, useRef, useState} from 'react';
import Layout from '../components/home/Layout';
import AppStrip from '../components/home/AppStrip';
import AppCard from '../components/home/AppCard';
import {apps} from '../data/apps';
import {useLang} from '../hooks/useLang';
import {homeJsonLd} from '../services/structuredData';
import {ogLanguage} from '../services/language';

const LINE_STEP = 80;

// The three rings from the old home page (desktop only)
const Rings = () => (
  <svg className="hero__rings" viewBox="0 0 60 318" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path fillRule="evenodd" clipRule="evenodd" d="M30 0C13.4315 0 0 13.4315 0 30V100C0 116.569 13.4315 130 30 130C46.5685 130 60 116.569 60 100V30C60 13.4315 46.5685 0 30 0ZM30 10C18.9543 10 10 18.9543 10 30V100C10 111.046 18.9543 120 30 120C41.0457 120 50 111.046 50 100V30C50 18.9543 41.0457 10 30 10Z" fill="#5A9DFD"/>
    <path fillRule="evenodd" clipRule="evenodd" d="M30 134C13.4315 134 0 147.431 0 164V224C0 240.569 13.4315 254 30 254C46.5685 254 60 240.569 60 224V164C60 147.431 46.5685 134 30 134ZM30 144C18.9543 144 10 152.954 10 164V224C10 235.046 18.9543 244 30 244C41.0457 244 50 235.046 50 224V164C50 152.954 41.0457 144 30 144Z" fill="#F2BB05"/>
    <path fillRule="evenodd" clipRule="evenodd" d="M30 258C13.4315 258 0 271.431 0 288C0 304.569 13.4315 318 30 318C46.5685 318 60 304.569 60 288C60 271.431 46.5685 258 30 258ZM30 268C18.9543 268 10 276.954 10 288C10 299.046 18.9543 308 30 308C41.0457 308 50 299.046 50 288C50 276.954 41.0457 268 30 268Z" fill="#E84855"/>
  </svg>
);

// Splits the lead into words and gives every rendered line its own reveal delay
const RevealText = ({text, firstLine, onLines}) => {
  const ref = useRef(null);
  const [lines, setLines] = useState([]);
  // Keep dashes and one-letter prepositions off the line edges
  const words = text
    .replace(/ —/g, '\u00A0—')
    .replace(/(^|\s)([вскиуоаВСКИУОАI]) /g, '$1$2\u00A0')
    .split(' ');

  useLayoutEffect(() => {
    let top = null;
    let line = 0;
    const result = [];
    ref.current.querySelectorAll('.hero__word').forEach((word) => {
      if (top !== null && word.offsetTop > top + 2) line++;
      top = word.offsetTop;
      result.push(line);
    });
    setLines(result);
    onLines(line + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <p className="hero__lead" ref={ref}>
      {words.map((word, i) => (
        <Fragment key={i}>
          <span className="hero__word reveal" style={{'--line': firstLine + (lines[i] || 0)}}>{word}</span>
          {i < words.length - 1 ? ' ' : null}
        </Fragment>
      ))}
    </p>
  );
};

const Home = () => {
  const {lang, t} = useLang();
  const [animate, setAnimate] = useState(true);
  const [leadLines, setLeadLines] = useState(4);
  const team = t('home.team', {returnObjects: true});
  const stats = t('home.stats', {returnObjects: true});
  const activeApps = apps.filter((app) => app.status === 'active');

  // Reveal animations run once, on first load
  useEffect(() => {
    const timer = setTimeout(() => setAnimate(false), 2500);
    return () => clearTimeout(timer);
  }, []);

  const leadLine = 1 + team.length;
  const noteLine = leadLine + leadLines;
  const statsLine = noteLine + 1;

  return (
    <Layout
      lang={lang}
      seo={{
        path: '/',
        title: t('home.title'),
        description: t('home.description'),
        image: `/og/home-${ogLanguage(lang)}.png`,
        imageAlt: t('common.ogImageAlt'),
        jsonLd: homeJsonLd(lang, t),
      }}
      className={animate ? 'home--animate' : ''}
      style={{'--step': `${LINE_STEP}ms`}}
    >
      <section className="hero">
        <div className="hero__rings-wrapper reveal" style={{'--line': 0}}>
          <Rings/>
        </div>
        <div className="hero__text">
          <h1 className="hero__name reveal" style={{'--line': 0}}>{t('home.teamTitle')}</h1>
          <ul className="team">
            {team.map((member, i) => (
              <li className="team__item reveal" key={member.role} style={{'--line': i + 1}}>
                <span className="team__dot" aria-hidden="true"/>
                <span className="team__role">{member.role}</span>
                {' '}
                <span className="team__name">— {member.name}</span>
              </li>
            ))}
          </ul>
          <RevealText text={t('home.lead')} firstLine={leadLine} onLines={setLeadLines}/>
          <p className="hero__note reveal" style={{'--line': noteLine}}>{t('home.note')}</p>
          <ul className="stats reveal" style={{'--line': statsLine}}>
            {stats.map((stat) => (
              <li className="stats__item" key={stat.label}>
                <span className="stats__value">
                  {stat.value.replace(/\+$/, '')}
                  <span className="stats__plus">+</span>
                </span>
                <span className="stats__label">{stat.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="home-apps" id="projects" style={{'--strip-start': statsLine + 1}}>
        <h2 className="home-apps__title" id="projects-title">{t('home.projectsTitle')}</h2>
        <AppStrip apps={apps}/>
        <div className="home-apps__cards">
          {activeApps.map((app) => <AppCard key={app.id} app={app}/>)}
        </div>
      </section>
    </Layout>
  );
};

export default Home;
